import * as React from 'react';
import classNames from 'classnames';
import {Flex} from '../Flex';
import {Label} from '../Label';
import {BackgroundColors} from '../Types';
import style from './text-field.scss';

export interface TextFieldProps {
  label?: string;
  value: string;
  error?: string;
  stacked?: boolean;
  touched?: boolean;
  helpText?: string;
  floating?: boolean;
  backgroundColor?: BackgroundColors;
  autocomplete?: string;
  ariaDescribedById?: string;
}

type InputProps = JSX.IntrinsicElements['input'];

export function TextField({
  id,
  label,
  value,
  error,
  stacked = true,
  touched = false,
  helpText,
  floating = false,
  className,
  backgroundColor = 'transparent',
  autocomplete,
  ariaDescribedById,
  ...restProps
}: TextFieldProps & InputProps) {
  const showHelp = Boolean(helpText);
  const showError = Boolean(touched && error);
  const showStandardLabel = label && !floating;
  const showStandardInput = !floating;
  const showFloatingCombo = label && floating;

  return (
    <Flex
      direction="column"
      className={classNames(className, {
        [style.stacked]: stacked,
        [style.error]: touched && error,
        [style.floating]: floating,
        [style[backgroundColor]]: backgroundColor,
      })}
    >
      {showStandardLabel && <Label htmlFor={id} label={String(label)} />}
      {showStandardInput && <input id={id} className={style.input} {...restProps} />}
      {showFloatingCombo && (
        <div className={style.floatingWrapper}>
          <input
            aria-label={String(label)}
            aria-describedby={ariaDescribedById}
            className={classNames(style.input, {[style.floated]: value})}
            id={id}
            autoComplete={autocomplete}
            {...restProps}
          />
          <Label
            htmlFor={id}
            label={String(label)}
            className={style.floatingLabel}
            ariaHidden
            inheritColor
          />
        </div>
      )}
      {showError && <span id={ariaDescribedById} className={style.errorText}>{error}</span>}
      {showHelp && <span className={style.helpText}>{helpText}</span>}
    </Flex>
  );
}
